import React from 'react';
import { MentorAreaConfig, MentorModule, MentorLesson } from '../../types/mentorship';
import { HeroVideoSpoiler } from './HeroVideoSpoiler';
import { ModulePosterCard } from './ModulePosterCard';
import { PlayCircle, Clock, CheckCircle2, ChevronRight, Layers, Sparkles } from 'lucide-react';

interface MentorshipCoursePageProps {
  config: MentorAreaConfig;
  onUpdateYoutubeUrl: (newUrl: string) => void;
  onSelectModule: (module: MentorModule) => void;
  onSelectLesson: (module: MentorModule, lesson: MentorLesson) => void;
}

export const MentorshipCoursePage: React.FC<MentorshipCoursePageProps> = ({
  config,
  onUpdateYoutubeUrl,
  onSelectModule,
  onSelectLesson,
}) => {
  // Aulas em Destaque selecionadas pelo Mentor (Primeiras aulas dos módulos principais)
  const featuredLessons = config.modules
    .flatMap((mod) => mod.lessons.map((les) => ({ module: mod, lesson: les })))
    .slice(0, 6);

  return (
    <div className="w-full flex flex-col gap-10 pb-16 animate-fade-in select-none">
      {/* 1. Full-Bleed Hero Video Header (Seamless background blend) */}
      <HeroVideoSpoiler
        config={config}
        onUpdateYoutubeUrl={onUpdateYoutubeUrl}
        onWatchClick={() => {
          if (config.modules[0]?.lessons[0]) {
            onSelectLesson(config.modules[0], config.modules[0].lessons[0]);
          }
        }}
      />

      {/* 2. Módulos Section (Hubla Grid com Padding Ajustado sem Cortes de Borda) */}
      <section className="flex flex-col gap-3 w-full">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#0FFF55]" />
            <h2 className="text-xl font-black text-white uppercase tracking-tight">
              Módulos
            </h2>
          </div>
          <span className="text-xs text-gray-400 font-medium">
            Clique em um módulo para ver todas as aulas
          </span>
        </div>

        {/* Full-width scrollable poster grid com padding px-4 py-4 prevenindo cortes de hover */}
        <div className="flex items-center gap-5 overflow-x-auto px-2 py-4 w-full scrollbar-thin scrollbar-thumb-[#0FFF55]/40">
          {config.modules.map((mod) => (
            <ModulePosterCard
              key={mod.id}
              module={mod}
              isSelected={false}
              onSelect={() => onSelectModule(mod)}
              accentColor={config.branding.accentColor || '#0FFF55'}
            />
          ))}
        </div>
      </section>

      {/* 3. Seção "Conteúdos em Destaque" (Editável pelo Mentor) */}
      <section className="flex flex-col gap-4 w-full bg-[#17181c] p-6 rounded-2xl border border-white/10 shadow-xl">
        <div className="flex items-center justify-between pb-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0FFF55]" />
            <h3 className="text-lg md:text-xl font-bold text-white uppercase tracking-tight">
              Conteúdos em Destaque
            </h3>
          </div>
          <span className="text-xs font-semibold text-gray-400">
            Aulas essenciais recomendadas
          </span>
        </div>

        {/* Grid de Cards de Aulas em Destaque */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pt-2">
          {featuredLessons.map(({ module: mod, lesson: les }) => (
            <div
              key={les.id}
              onClick={() => onSelectLesson(mod, les)}
              className="group relative flex flex-col rounded-xl overflow-hidden bg-[#121316] border border-white/10 hover:border-[#0FFF55]/60 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 shadow-md"
            >
              {/* Thumbnail */}
              <div className="relative w-full aspect-video overflow-hidden bg-black">
                <img
                  src={les.thumbnailUrl}
                  alt={les.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#0FFF55] text-black flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                    <PlayCircle className="w-6 h-6 fill-black text-black" />
                  </div>
                </div>
                {les.isCompleted && (
                  <span className="absolute top-2 right-2 p-1 rounded-full bg-emerald-500 text-black shadow-md">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-3 flex flex-col justify-between flex-1 gap-2">
                <div>
                  <span className="text-[9px] font-bold text-[#0FFF55] uppercase tracking-wider block mb-1">
                    {mod.title}
                  </span>
                  <h4 className="text-xs font-bold text-white group-hover:text-[#0FFF55] line-clamp-2 transition-colors uppercase">
                    {les.title}
                  </h4>
                </div>

                <div className="flex items-center justify-between text-[10px] text-gray-400 font-medium pt-1 border-t border-white/5">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-gray-500" />
                    {les.durationText}
                  </span>
                  <span className="flex items-center gap-0.5 text-[#0FFF55] opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                    Assistir <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
